import type { CSSProperties } from 'react';
import { WEDGE_SHELL as B } from '@territory/tokens';
import { control, font, radius, space, TOUCH_MIN } from '../scale.js';

export type WedgeAuthFieldVariant = 'wedge-auth' | 'wedge-auth-recipient' | 'wedge-auth-pin' | 'wedge-auth-account' | 'wedge-auth-magic';
/* Released brandField and Magic recipient geometry and timing, off-scale. */
const FIELD_PY = 13;
const FIELD_PX = 15;
const FIELD_MS = 200;
// Released Landing insets, off-scale. Password visibility and valid-email artwork stay in the caller.
const LANDING_PX = 14;
const PASSWORD_INSET = 62;
const MAGIC_INSET = 38;

const MAGIC = '[data-rv-auth-field="wedge-auth-magic"]';
export const WEDGE_AUTH_FIELD_CSS = `${MAGIC}:focus { border-color: ${B.fieldFocusBorder} !important; box-shadow: ${B.magicFieldFocusShadow} !important; }
${MAGIC}::placeholder { color: ${B.magicFieldPlaceholder}; }`;

export interface WedgeAuthFieldOptions {
  size?: 'touch' | 'recovery';
  focus?: 'flat';
  motion?: 'instant';
  inset?: 'password';
  border?: 'warning';
}

export function wedgeAuthFieldStyle(variant: WedgeAuthFieldVariant, focused: boolean, options: WedgeAuthFieldOptions = {}): CSSProperties {
  if (variant === 'wedge-auth-account' || variant === 'wedge-auth-magic') {
    const magic = variant === 'wedge-auth-magic';
    return {
      width: '100%',
      boxSizing: 'border-box',
      borderRadius: radius.lg,
      color: B.text,
      fontSize: font.callout,
      border: `1px solid ${options.border === 'warning' ? B.fieldWarningBorder : B.border}`,
      background: B.surface,
      padding: `${FIELD_PX}px ${magic ? MAGIC_INSET : options.inset === 'password' ? PASSWORD_INSET : LANDING_PX}px ${FIELD_PX}px ${LANDING_PX}px`,
      minHeight: control.field,
      ...(magic ? { fontFamily: 'inherit', outline: 'none' } : {}),
    };
  }
  const shared: CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    borderRadius: radius.lg,
    color: B.text,
    fontSize: font.callout,
    outline: 'none',
  };
  if (variant === 'wedge-auth-recipient') return {
    ...shared,
    padding: `${FIELD_PY}px ${space[4]}px`,
    minHeight: TOUCH_MIN,
    border: `1px solid ${B.recipientBorder}`,
    background: B.recipientSurface,
  };
  return {
    ...shared,
    marginBottom: space[3],
    padding: `${FIELD_PY}px ${FIELD_PX}px`,
    border: `1px solid ${focused ? B.fieldFocusBorder : B.border}`,
    background: focused ? B.fieldFocusSurface : B.surface,
    fontFamily: 'inherit',
    transition: options.motion === 'instant' ? 'initial' : `all ${FIELD_MS}ms ${B.ease}`,
    boxShadow: focused && options.focus !== 'flat' && variant !== 'wedge-auth-pin' ? B.fieldFocusShadow : 'none',
    ...(options.size === 'touch' ? { minHeight: TOUCH_MIN } : {}),
    ...(options.size === 'recovery' ? { padding: `${LANDING_PX}px ${FIELD_PX}px`, minHeight: control.field } : {}),
    ...(variant === 'wedge-auth-pin' ? { fontFamily: B.nativePinFont, textAlign: 'center', letterSpacing: space[1] } : {}),
  };
}
